import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { deleteCallRecordings, getCall, getCallEvents, getMetrics, getSettings, getTranscriptUrl, getVoices, listCalls, listConnectionAttempts, setDefaultVoice, updateSettings, updateVoices } from './api'
import type { AdminSettings, CallDetail, CallEvent, CallListItem, ConnectionAttempt, MetricsResponse, Voice } from './types'

type View = 'overview' | 'calls' | 'issues' | 'detail' | 'settings' | 'voices'
type RecordingTrack = 'real_conversation' | 'combined' | 'customer' | 'assistant'

const RECORDING_TRACKS: Array<{ id: RecordingTrack; label: string }> = [
  { id: 'real_conversation', label: 'Real conversation' },
  { id: 'customer', label: 'Customer' },
  { id: 'assistant', label: 'Assistant generated' },
  { id: 'combined', label: 'Combined legacy' },
]

function getRoute(): { view: View; callId?: string } {
  const path = window.location.pathname
  const detailMatch = path.match(/^\/admin\/calls\/([^/]+)/)
  if (detailMatch) return { view: 'detail', callId: detailMatch[1] }
  if (path.startsWith('/admin/settings')) return { view: 'settings' }
  if (path.startsWith('/admin/voices')) return { view: 'voices' }
  if (path.startsWith('/admin/issues')) return { view: 'issues' }
  if (path.startsWith('/admin/calls')) return { view: 'calls' }
  return { view: 'overview' }
}

export function AdminApp() {
  const [route, setRoute] = useState(getRoute)

  useEffect(() => {
    const onPopState = () => setRoute(getRoute())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    setRoute(getRoute())
  }, [])

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <div className="admin-brand-mark">AP</div>
          <div>
            <strong>Voice Ops</strong>
            <span>Activity Park</span>
          </div>
        </div>

        <nav className="admin-nav" aria-label="Admin navigation">
          <NavButton active={route.view === 'overview'} label="Overview" onClick={() => navigate('/admin')} />
          <NavButton active={route.view === 'calls' || route.view === 'detail'} label="Calls" onClick={() => navigate('/admin/calls')} />
          <NavButton active={route.view === 'issues'} label="Issues" onClick={() => navigate('/admin/issues')} />
          <NavButton active={route.view === 'voices'} label="Voices" onClick={() => navigate('/admin/voices')} />
          <NavButton active={route.view === 'settings'} label="Settings" onClick={() => navigate('/admin/settings')} />
        </nav>

        <div className="admin-sidebar-footer">
          <span>AI analysis</span>
          <button className="admin-sidebar-link" onClick={() => navigate('/admin/settings')}>Configure</button>
        </div>
      </aside>

      <main className="admin-main">
        {route.view === 'overview' && <Overview navigate={navigate} />}
        {route.view === 'calls' && <CallsView navigate={navigate} />}
        {route.view === 'issues' && <IssuesView navigate={navigate} />}
        {route.view === 'voices' && <VoicesView />}
        {route.view === 'settings' && <SettingsView />}
        {route.view === 'detail' && route.callId && <CallDetailView callId={route.callId} navigate={navigate} />}
      </main>
    </div>
  )
}

function NavButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button className={`admin-nav-item ${active ? 'active' : ''}`} onClick={onClick}>
      <span className="admin-nav-dot" />
      {label}
    </button>
  )
}

function Overview({ navigate }: { navigate: (path: string) => void }) {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null)
  const [recentCalls, setRecentCalls] = useState<CallListItem[]>([])
  const [period, setPeriod] = useState('all')
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10))
  const [error, setError] = useState('')

  useEffect(() => {
    setError('')
    Promise.all([getMetrics({ period, day: period === 'day' ? day : undefined }), listCalls({ limit: 6 })])
      .then(([nextMetrics, calls]) => {
        setMetrics(nextMetrics)
        setRecentCalls(calls.items)
      })
      .catch(err => setError(err.message))
  }, [day, period])

  if (error) return <AdminError title="Admin API error" message={error} />
  if (!metrics) return <AdminLoading />
  if (!metrics.configured) return <AdminError title="Storage is not configured" message="Set DATABASE_URL and restart the backend." />

  const totals = metrics.totals!
  return (
    <section className="admin-page">
      <AdminHeader title="Voice agent dashboard" subtitle="Calls, recordings, transcripts and technical errors." />
      <PeriodControls period={period} setPeriod={setPeriod} day={day} setDay={setDay} />

      <div className="metric-grid">
        <MetricCard label="Total calls" value={totals.total_calls} />
        <MetricCard label="Total call time" value={formatDuration(totals.total_duration_seconds)} />
        <MetricCard label="Avg duration" value={formatDuration(totals.average_duration_seconds)} />
        <MetricCard label="Estimated cost" value={formatMoney(totals.total_estimated_cost, metrics.settings?.billing_currency || 'EUR')} />
        <MetricCard label="Active now" value={totals.active_calls} tone="success" />
        <MetricCard label="Errors" value={totals.total_errors} tone="danger" />
      </div>

      <div className="admin-grid-two">
        <Panel title="Call volume">
          <div className="bar-list">
            {(metrics.calls_by_day || []).map(item => (
              <div className="bar-row" key={item.day}>
                <span>{formatDay(item.day)}</span>
                <div><i style={{ width: `${Math.max(8, item.count * 18)}px` }} /></div>
                <strong>{item.count}</strong>
              </div>
            ))}
            {(metrics.calls_by_day || []).length === 0 && <EmptyText>No calls yet.</EmptyText>}
          </div>
        </Panel>

        <Panel title="Status mix">
          <div className="status-stack">
            {(metrics.calls_by_status || []).map(item => (
              <StatusPill key={item.status} label={item.status} count={item.count} />
            ))}
            {(metrics.calls_by_status || []).length === 0 && <EmptyText>No statuses yet.</EmptyText>}
          </div>
        </Panel>
      </div>

      <Panel title="Recent calls" action={<button className="admin-link-btn" onClick={() => navigate('/admin/calls')}>View all</button>}>
        <CallsTable calls={recentCalls} onOpen={id => navigate(`/admin/calls/${id}`)} />
      </Panel>
    </section>
  )
}

function CallsView({ navigate }: { navigate: (path: string) => void }) {
  const [calls, setCalls] = useState<CallListItem[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [lang, setLang] = useState('')
  const [error, setError] = useState('')

  const loadCalls = useCallback(() => {
    listCalls({ search, status, lang, limit: 80 })
      .then(result => setCalls(result.items))
      .catch(err => setError(err.message))
  }, [lang, search, status])

  useEffect(() => {
    loadCalls()
  }, [loadCalls])

  return (
    <section className="admin-page">
      <AdminHeader title="Calls" subtitle="Search recordings, transcripts and call outcomes." />
      <Filters
        search={search}
        setSearch={setSearch}
        status={status}
        setStatus={setStatus}
        lang={lang}
        setLang={setLang}
      />
      {error ? <AdminError title="Could not load calls" message={error} /> : (
        <Panel title={`${calls.length} calls`}>
          <CallsTable calls={calls} onOpen={id => navigate(`/admin/calls/${id}`)} />
        </Panel>
      )}
    </section>
  )
}

function IssuesView({ navigate }: { navigate: (path: string) => void }) {
  const [calls, setCalls] = useState<CallListItem[]>([])
  const [attempts, setAttempts] = useState<ConnectionAttempt[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      listCalls({ status: 'error', limit: 80 }),
      listConnectionAttempts({ problemOnly: true, limit: 80 }),
    ])
      .then(([callResult, attemptResult]) => {
        setCalls(callResult.items)
        setAttempts(attemptResult.items)
      })
      .catch(err => setError(err.message))
  }, [])

  return (
    <section className="admin-page">
      <AdminHeader title="Issues" subtitle="Failed calls, rejected attempts and conversations with technical errors." />
      {error ? <AdminError title="Could not load issues" message={error} /> : (
        <div className="issues-stack">
          <Panel title={`${calls.length} failed calls`}>
            <CallsTable calls={calls} onOpen={id => navigate(`/admin/calls/${id}`)} />
          </Panel>
          <Panel title={`${attempts.length} failed connection attempts`}>
            <AttemptsTable attempts={attempts} onOpenCall={id => navigate(`/admin/calls/${id}`)} />
          </Panel>
        </div>
      )}
    </section>
  )
}

function VoicesView() {
  const [voices, setVoices] = useState<Voice[]>([])
  const [defaultVoice, setDefaultVoiceState] = useState('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [bulk, setBulk] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setError('')
    getVoices()
      .then(result => {
        setVoices(result.voices)
        setDefaultVoiceState(result.default_voice)
      })
      .catch(err => setError(err.message))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const flash = (text: string) => {
    setSaved(text)
    window.setTimeout(() => setSaved(''), 2500)
  }

  const handleSetDefault = async (id: string) => {
    setSaving(true)
    setError('')
    try {
      const result = await setDefaultVoice(id)
      setVoices(result.voices)
      setDefaultVoiceState(result.default_voice)
      flash(`Default voice set to ${id}.`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (id: string) => {
    if (!window.confirm(`Remove voice "${id}" from the catalog?`)) return
    setSaving(true)
    setError('')
    try {
      const remaining = voices.filter(voice => voice.id !== id)
      const result = await updateVoices(remaining)
      setVoices(result.voices)
      setDefaultVoiceState(result.default_voice)
      flash(`Removed ${id}.`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleAddBulk = async () => {
    const lines = bulk.split('\n').map(line => line.trim()).filter(Boolean)
    if (lines.length === 0) {
      setError('Paste at least one line. Format: id,label[,accent]')
      return
    }
    const parsed: Voice[] = []
    const skipped: string[] = []
    for (const line of lines) {
      const parts = line.split(',').map(part => part.trim())
      if (parts.length < 2 || !parts[0]) {
        skipped.push(line)
        continue
      }
      const voice: Voice = { id: parts[0].toLowerCase(), label: parts[1] }
      if (parts[2]) voice.accent = parts[2].toLowerCase()
      parsed.push(voice)
    }
    if (parsed.length === 0) {
      setError('No valid lines. Use: id,label[,accent]')
      return
    }
    setSaving(true)
    setError('')
    try {
      const index = new Map(voices.map(voice => [voice.id, voice]))
      for (const voice of parsed) index.set(voice.id, voice)
      const result = await updateVoices(Array.from(index.values()))
      setVoices(result.voices)
      setDefaultVoiceState(result.default_voice)
      setBulk('')
      const suffix = skipped.length > 0 ? ` (skipped ${skipped.length} invalid)` : ''
      flash(`Added ${parsed.length} voice(s)${suffix}.`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const defaultVoiceValid = defaultVoice && voices.some(voice => voice.id === defaultVoice)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  return (
    <section className="admin-page">
      <AdminHeader title="Voices" subtitle="Manage the voice catalog and default voice for new calls." />
      {error && <AdminError title="Voice catalog error" message={error} />}
      {saved && <div className="admin-flash">{saved}</div>}

      <Panel
        title="Add voices"
        subtitle="One per line: id,label[,accent]. Accent is informational (e.g. de, ru, en). Existing ids are updated."
        action={(
          <button className="admin-primary-btn" disabled={saving || !bulk.trim()} onClick={handleAddBulk}>
            {saving ? 'Saving...' : 'Add voices'}
          </button>
        )}
      >
        <textarea
          className="voice-bulk-input"
          value={bulk}
          onChange={event => setBulk(event.target.value)}
          placeholder={'ara,Ara,de\neve,Eve,en\n458705c07139,Custom 1,de'}
          rows={5}
        />
      </Panel>

      <Panel
        title={`${voices.length} voice(s)`}
        action={defaultVoice
          ? <span className="admin-meta">Default: <code>{defaultVoice}</code>{!defaultVoiceValid && ' (not in catalog — falling back to env default)'}</span>
          : <span className="admin-meta">Default: env (<code>VOICE</code> in <code>.env</code>)</span>}
      >
        {voices.length === 0 ? (
          <EmptyText>No voices in the catalog. Add one above — it will be accepted on the next WebSocket connection without restarting the server.</EmptyText>
        ) : (
          <div className="voice-grid">
            {voices.map(voice => {
              const isDefault = voice.id === defaultVoice
              return (
                <article key={voice.id} className={`voice-card ${isDefault ? 'is-default' : ''}`}>
                  <header className="voice-card-head">
                    <strong>{voice.label}</strong>
                    {voice.accent && <span className="voice-accent-badge">{voice.accent}</span>}
                    {isDefault && <span className="voice-default-badge">Default</span>}
                  </header>
                  <code className="voice-card-id">{voice.id}</code>
                  <div className="voice-card-actions">
                    <a className="admin-action-btn" href={`/?lang=de&voice=${encodeURIComponent(voice.id)}`} target="_blank" rel="noopener noreferrer">
                      Test
                    </a>
                    {!isDefault && (
                      <button className="admin-action-btn" disabled={saving} onClick={() => handleSetDefault(voice.id)}>
                        Set default
                      </button>
                    )}
                    <button className="admin-danger-btn" disabled={saving} onClick={() => handleRemove(voice.id)}>
                      Remove
                    </button>
                  </div>
                  <p className="voice-card-hint">
                    Test opens <code>{origin}/?lang=de&amp;voice={voice.id}</code> in a new tab.
                  </p>
                </article>
              )
            })}
          </div>
        )}
      </Panel>
    </section>
  )
}

function CallDetailView({ callId, navigate }: { callId: string; navigate: (path: string) => void }) {
  const [call, setCall] = useState<CallDetail | null>(null)
  const [events, setEvents] = useState<CallEvent[]>([])
  const [activeTrack, setActiveTrack] = useState<RecordingTrack>('real_conversation')
  const [error, setError] = useState('')
  const [actionMessage, setActionMessage] = useState('')

  useEffect(() => {
    Promise.all([getCall(callId), getCallEvents(callId)])
      .then(([nextCall, nextEvents]) => {
        setCall(nextCall)
        setEvents(nextEvents.items)
      })
      .catch(err => setError(err.message))
  }, [callId])

  useEffect(() => {
    if (!call) return
    if (call.artifacts.recording[activeTrack]) return
    const fallback = RECORDING_TRACKS.find(track => call.artifacts.recording[track.id])
    if (fallback) setActiveTrack(fallback.id)
  }, [activeTrack, call])

  const activeRecording = call?.artifacts.recording[activeTrack]
  const hasAnyRecording = Boolean(call && Object.values(call.artifacts.recording).some(Boolean))
  const realRecordingDuration = call?.artifacts.recording_duration_seconds?.real_conversation

  const reloadCall = () => {
    Promise.all([getCall(callId), getCallEvents(callId)])
      .then(([nextCall, nextEvents]) => {
        setCall(nextCall)
        setEvents(nextEvents.items)
      })
      .catch(err => setError(err.message))
  }

  const handleDeleteRecordings = () => {
    if (!call || !window.confirm('Delete audio recordings for this call? Logs, transcript, duration and cost will stay.')) return
    setActionMessage('')
    deleteCallRecordings(call.id)
      .then(result => {
        setActionMessage(result.deleted_tracks.length > 0 ? 'Recordings deleted.' : 'No recording files were present.')
        setActiveTrack('real_conversation')
        reloadCall()
      })
      .catch(err => setActionMessage(err.message))
  }

  if (error) return <AdminError title="Could not load call" message={error} />
  if (!call) return <AdminLoading />

  return (
    <section className="admin-page">
      <button className="admin-back" onClick={() => navigate('/admin/calls')}>Back to calls</button>
      <AdminHeader title={`Call ${call.id.slice(0, 8)}`} subtitle={`${formatDate(call.started_at)} · ${call.lang || 'unknown'} · ${call.ended_reason || call.status}`} />

      <div className="call-detail-grid">
        <div className="call-detail-main">
          <Panel
            title="Recording"
            action={(
              <div className="panel-actions">
                {activeRecording && <a className="admin-action-btn" href={activeRecording} download>Download audio</a>}
                {hasAnyRecording && <button className="admin-danger-btn" onClick={handleDeleteRecordings}>Delete audio</button>}
              </div>
            )}
          >
            <div className="recording-tabs">
              {RECORDING_TRACKS.map(track => (
                <button
                  key={track.id}
                  className={activeTrack === track.id ? 'active' : ''}
                  disabled={!call.artifacts.recording[track.id]}
                  onClick={() => setActiveTrack(track.id)}
                >
                  {track.label}
                </button>
              ))}
            </div>
            {!call.artifacts.recording.real_conversation && call.artifacts.recording.combined && (
              <p className="recording-note">This older call has no real conversation recording. Combined legacy is a technical mix, not exact heard audio.</p>
            )}
            {activeRecording ? (
              <audio className="admin-audio" controls src={activeRecording} />
            ) : (
              <EmptyText>No {activeTrack} recording for this call.</EmptyText>
            )}
            {actionMessage && <p className="action-message">{actionMessage}</p>}
          </Panel>

          <Panel title="Transcript" action={<a className="admin-action-btn" href={getTranscriptUrl(call.id)} download>Download transcript</a>}>
            <div className="transcript-timeline">
              {call.messages.map((message, index) => (
                <article className={`transcript-item ${message.role}`} key={`${message.role}-${index}`}>
                  <time>{formatSeconds(message.seconds_from_start)}</time>
                  <div>
                    <strong>{message.role === 'assistant' ? 'Assistant' : 'Customer'}</strong>
                    {message.role === 'assistant' && message.source_event?.startsWith('assistant.playback.transcript') && (
                      <span className="transcript-meta">
                        Heard transcript
                        {message.duration ? ` · ${formatDuration(message.duration)}` : ''}
                        {message.source_event.includes('interrupted') ? ' · Interrupted' : ''}
                      </span>
                    )}
                    <p>{message.message}</p>
                  </div>
                </article>
              ))}
              {call.messages.length === 0 && <EmptyText>No transcript messages captured.</EmptyText>}
            </div>
          </Panel>
        </div>

        <aside className="call-detail-side">
          <Panel title="Call facts">
            <Fact label="Status" value={call.status} />
            <Fact label="Session duration" value={formatDuration(call.duration_seconds || 0)} />
            <Fact label="Real recording" value={realRecordingDuration ? formatDuration(realRecordingDuration) : (call.artifacts.recording.real_conversation ? 'Available' : 'Not captured')} />
            <Fact label="Estimated cost" value={formatMoney(call.estimated_cost || 0, call.billing_currency || 'EUR')} />
            <Fact label="Recording" value={call.recording_enabled === false ? 'Disabled' : 'Enabled'} />
            <Fact label="Errors" value={String(call.error_count)} />
            <Fact label="Messages" value={String(call.message_count)} />
            <Fact label="Consent" value={call.recording_consent_announced ? 'Announced' : 'Unknown'} />
          </Panel>

          <Panel title="AI analysis">
            <div className="analysis-placeholder">
              <strong>{call.analysis_status === 'not_configured' ? 'Not configured' : call.analysis_status || 'Pending'}</strong>
              <p>Configure provider, model and analysis mode in Settings. Transcript and recordings are ready for the future analysis pipeline.</p>
              <button className="admin-action-btn" onClick={() => navigate('/admin/settings')}>Configure</button>
            </div>
          </Panel>

          <Panel title={`Technical events (${events.length})`}>
            <div className="event-list">
              {events.map((event, index) => {
                const detail = formatEventDetail(event)
                return (
                <div className={`event-row ${event.severity}`} key={`${event.event_type}-${index}`}>
                  <span>{formatSeconds(event.seconds_from_start)}</span>
                  <strong>
                    {event.event_type}
                    {detail && <small>{detail}</small>}
                  </strong>
                  <em>{event.direction}</em>
                </div>
                )
              })}
              {events.length === 0 && <EmptyText>No events captured.</EmptyText>}
            </div>
          </Panel>
        </aside>
      </div>
    </section>
  )
}

function SettingsView() {
  const [settings, setSettings] = useState<AdminSettings | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getSettings()
      .then(result => setSettings(result.settings))
      .catch(err => setError(err.message))
  }, [])

  const setField = <Key extends keyof AdminSettings,>(key: Key, value: AdminSettings[Key]) => {
    setSettings(current => current ? { ...current, [key]: value } : current)
    setSaved('')
  }

  const save = () => {
    if (!settings) return
    setSaving(true)
    setError('')
    updateSettings(settings)
      .then(result => {
        setSettings(result.settings)
        setSaved('Settings saved')
      })
      .catch(err => setError(err.message))
      .finally(() => setSaving(false))
  }

  if (error && !settings) return <AdminError title="Could not load settings" message={error} />
  if (!settings) return <AdminLoading />

  return (
    <section className="admin-page">
      <AdminHeader title="Settings" subtitle="Recording, billing and AI analysis configuration." />

      <div className="settings-grid">
        <Panel title="Recording">
          <div className="settings-form">
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={settings.recording_enabled}
                onChange={event => setField('recording_enabled', event.target.checked)}
              />
              <span>
                <strong>Record calls</strong>
                <em>When disabled, calls still work but WAV artifacts are not stored.</em>
              </span>
            </label>
            <label>
              <span>Recording retention days</span>
              <input
                type="number"
                min="1"
                step="1"
                value={settings.recording_retention_days}
                onChange={event => setField('recording_retention_days', Number(event.target.value))}
              />
            </label>
          </div>
        </Panel>

        <Panel title="Billing">
          <div className="settings-form">
            <label>
              <span>Price per minute</span>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={settings.billing_price_per_minute}
                onChange={event => setField('billing_price_per_minute', Number(event.target.value))}
              />
            </label>
            <label>
              <span>Currency</span>
              <select value={settings.billing_currency} onChange={event => setField('billing_currency', event.target.value)}>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </label>
          </div>
        </Panel>

        <Panel title="AI analysis">
          <div className="settings-form">
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={settings.ai_analysis_enabled}
                onChange={event => setField('ai_analysis_enabled', event.target.checked)}
              />
              <span>
                <strong>Enable analysis module</strong>
                <em>This configures the future analysis workflow; automatic generation is not implemented yet.</em>
              </span>
            </label>
            <label>
              <span>Provider</span>
              <select value={settings.ai_provider} onChange={event => setField('ai_provider', event.target.value)}>
                <option value="xai">xAI</option>
              </select>
            </label>
            <label>
              <span>Model</span>
              <input value={settings.ai_model} onChange={event => setField('ai_model', event.target.value)} placeholder="Model for future call analysis" />
            </label>
            <label>
              <span>Analysis prompt</span>
              <textarea value={settings.ai_prompt} onChange={event => setField('ai_prompt', event.target.value)} placeholder="Quality criteria, issue categories, next actions..." />
            </label>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={settings.ai_auto_analyze}
                onChange={event => setField('ai_auto_analyze', event.target.checked)}
              />
              <span>
                <strong>Auto analyze after call</strong>
                <em>Reserved for the next implementation step.</em>
              </span>
            </label>
          </div>
        </Panel>
      </div>

      <div className="settings-savebar">
        {error && <span className="settings-error">{error}</span>}
        {saved && <span className="settings-saved">{saved}</span>}
        <button className="admin-primary-btn" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save settings'}</button>
      </div>
    </section>
  )
}

function PeriodControls(props: {
  period: string
  setPeriod: (value: string) => void
  day: string
  setDay: (value: string) => void
}) {
  return (
    <div className="period-controls">
      <button className={props.period === 'all' ? 'active' : ''} onClick={() => props.setPeriod('all')}>All time</button>
      <button className={props.period === '90d' ? 'active' : ''} onClick={() => props.setPeriod('90d')}>90 days</button>
      <button className={props.period === '30d' ? 'active' : ''} onClick={() => props.setPeriod('30d')}>30 days</button>
      <button className={props.period === 'today' ? 'active' : ''} onClick={() => props.setPeriod('today')}>Today</button>
      <button className={props.period === 'day' ? 'active' : ''} onClick={() => props.setPeriod('day')}>Day</button>
      {props.period === 'day' && <input type="date" value={props.day} onChange={event => props.setDay(event.target.value)} />}
    </div>
  )
}

function Filters(props: {
  search: string
  setSearch: (value: string) => void
  status: string
  setStatus: (value: string) => void
  lang: string
  setLang: (value: string) => void
}) {
  return (
    <div className="admin-filters">
      <input value={props.search} onChange={event => props.setSearch(event.target.value)} placeholder="Search call id, reason or transcript" />
      <select value={props.status} onChange={event => props.setStatus(event.target.value)}>
        <option value="">All statuses</option>
        <option value="active">Active</option>
        <option value="completed">Completed</option>
        <option value="error">Error</option>
      </select>
      <select value={props.lang} onChange={event => props.setLang(event.target.value)}>
        <option value="">All languages</option>
        <option value="de">German</option>
        <option value="ru">Russian</option>
      </select>
    </div>
  )
}

function CallsTable({ calls, onOpen }: { calls: CallListItem[]; onOpen: (id: string) => void }) {
  if (calls.length === 0) return <EmptyText>No calls match this view.</EmptyText>

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Call</th>
            <th>Status</th>
            <th>Duration</th>
            <th>Cost</th>
            <th>Messages</th>
            <th>Artifacts</th>
          </tr>
        </thead>
        <tbody>
          {calls.map(call => (
            <tr key={call.id} onClick={() => onOpen(call.id)}>
              <td>{formatDate(call.started_at)}</td>
              <td>
                <strong>{call.id.slice(0, 10)}</strong>
                <span>{call.ended_reason || call.voice || 'voice call'}</span>
              </td>
              <td><StatusBadge status={call.status} errors={call.error_count} /></td>
              <td>{formatDuration(call.duration_seconds || 0)}</td>
              <td>{formatMoney(call.estimated_cost || 0, call.billing_currency || 'EUR')}</td>
              <td>{call.message_count}</td>
              <td>{call.has_recording ? 'Recording' : 'Transcript'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AttemptsTable({ attempts, onOpenCall }: { attempts: ConnectionAttempt[]; onOpenCall: (id: string) => void }) {
  if (attempts.length === 0) return <EmptyText>No failed attempts captured.</EmptyText>

  return (
    <div className="admin-table-wrap">
      <table className="admin-table attempts-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Status</th>
            <th>Reason</th>
            <th>Lang</th>
            <th>Call</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map(attempt => (
            <tr
              key={attempt.id}
              className={attempt.call_id ? '' : 'no-click'}
              onClick={() => attempt.call_id && onOpenCall(attempt.call_id)}
            >
              <td>{formatDate(attempt.started_at)}</td>
              <td><StatusBadge status={attempt.status} errors={attempt.status === 'error' ? 1 : 0} /></td>
              <td>
                <strong>{attempt.reject_reason || 'unknown'}</strong>
                <span>{attempt.error_message || attempt.id.slice(0, 10)}</span>
              </td>
              <td>{attempt.lang || '—'}</td>
              <td>{attempt.call_id ? attempt.call_id.slice(0, 10) : 'No call'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <header className="admin-header">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
    </header>
  )
}

function Panel({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2>{title}</h2>
          {subtitle && <p className="admin-panel-subtitle">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function MetricCard({ label, value, tone }: { label: string; value: string | number; tone?: 'success' | 'danger' }) {
  return (
    <div className={`metric-card ${tone || ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function StatusPill({ label, count }: { label: string; count: number }) {
  return (
    <div className="status-pill">
      <span>{label}</span>
      <strong>{count}</strong>
    </div>
  )
}

function StatusBadge({ status, errors }: { status: string; errors: number }) {
  return <span className={`status-badge ${status}`}>{errors > 0 ? `${status} · ${errors}` : status}</span>
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function AdminLoading() {
  return <div className="admin-loading">Loading admin data...</div>
}

function AdminError({ title, message }: { title: string; message: string }) {
  return (
    <div className="admin-error">
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
  )
}

function EmptyText({ children }: { children: ReactNode }) {
  return <p className="empty-text">{children}</p>
}

function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds)) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
}

function formatSeconds(seconds?: number | null) {
  if (seconds === null || seconds === undefined) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0')
  return `${mins}:${secs}`
}

function formatEventDetail(event: CallEvent) {
  const item = parseEventPayload(event.payload_redacted)
  if (!item) return ''

  if (event.event_type === 'call.ended') {
    return [item.status, item.ended_reason].filter(Boolean).join(' · ')
  }

  const message = item.message || item.error?.message
  if (message) return String(message)

  if (typeof item.name === 'string') return item.name
  if (typeof item.item?.name === 'string') return item.item.name
  return ''
}

function parseEventPayload(payload: unknown): Record<string, any> | null {
  if (!payload) return null
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, any>
        : null
    } catch {
      return null
    }
  }
  return typeof payload === 'object' && !Array.isArray(payload)
    ? payload as Record<string, any>
    : null
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatDay(value: string) {
  return new Intl.DateTimeFormat('en', { month: 'short', day: '2-digit' }).format(new Date(value))
}

function formatMoney(value: number, currency: string) {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0)
  } catch {
    return `${(Number.isFinite(value) ? value : 0).toFixed(2)} ${currency || ''}`.trim()
  }
}
